// Phase 10 (Track 2) — in-app documentation content.
// Each doc is a list of typed blocks rendered by Docs.jsx. No markdown dependency.

const b = {
  h: (text) => ({ type: "h", text }),
  p: (text) => ({ type: "p", text }),
  ul: (items) => ({ type: "ul", items }),
  ol: (items) => ({ type: "ol", items }),
  code: (text) => ({ type: "code", text }),
  note: (text) => ({ type: "note", text }),
};

const SECTOR_PACKS = {
  saas: { name: "SaaS", metrics: ["MRR", "NRR", "GRR", "Churn Rate", "CAC", "LTV", "Activation Rate"], q: "What was MRR last month by plan?" },
  healthcare: { name: "Healthcare", metrics: ["Occupancy Rate", "Average Length of Stay", "30-day Readmission Rate", "Denial Rate", "Average Daily Census"], q: "Which facility has the highest 30-day readmission rate?" },
  finserv: { name: "FinServ", metrics: ["AUM", "Net Interest Margin", "NPL Ratio", "CET1 Ratio", "Default Rate"], q: "Show the NPL ratio trend over the last 12 months." },
  retail: { name: "Retail", metrics: ["Net Sales", "Same-Store Sales Growth", "Average Basket Size", "Inventory Turn", "Shrink Rate"], q: "What were same-store sales by region last quarter?" },
  logistics: { name: "Logistics", metrics: ["On-Time Delivery Rate", "OTIF Rate", "Cost per Shipment", "Average Dwell Time"], q: "What is our on-time delivery rate by carrier?" },
  manufacturing: { name: "Manufacturing", metrics: ["OEE", "First Pass Yield", "Scrap Rate", "MTBF", "MTTR"], q: "Show OEE by line for the past month." },
};

function sectorQuickstart(slug) {
  const s = SECTOR_PACKS[slug];
  return {
    slug: `quickstart-${slug}`,
    title: `${s.name} quickstart`,
    group: "Sector quickstarts",
    blocks: [
      b.p(`Get a ${s.name} workspace answering questions in under ten minutes. When you pick "${s.name}" during onboarding, Sema installs the ${s.name} semantic pack so the glossary already speaks your language.`),
      b.h("1. Pick your sector"),
      b.p(`On the onboarding wizard choose ${s.name}. This loads draft glossary terms and governed metric candidates such as:`),
      b.ul(s.metrics),
      b.h("2. Connect data (or explore the demo)"),
      b.p("Connect a Postgres source, or click \u201cExplore demo data\u201d to load the bundled sample warehouse. Discovery profiles your schema and generates audit-grade definitions."),
      b.h("3. Promote pack terms"),
      b.p("Open Glossary, filter by the \u201cbusiness\u201d kind, review the draft terms and promote the ones you use to Active. Do the same for metrics under Semantics \u2192 Metrics."),
      b.h("4. Ask your first question"),
      b.code(s.q),
      b.p("Sema plans the SQL grounded in your glossary and verified joins, runs it read-only, and auto-charts the answer."),
      b.note("Active metrics resolve by name and synonyms in chat, and add a confidence boost to the plan."),
    ],
  };
}

export const DOCS = [
  {
    slug: "quickstart",
    title: "Quickstart",
    group: "Getting started",
    blocks: [
      b.p("Sema is the semantic layer for enterprise AI: a governed glossary, verified joins and first-class metrics that let anyone ask questions in plain language and trust the answer."),
      b.h("The five-minute path"),
      b.ol([
        "Sign up and pick your sector \u2014 Sema installs a matching semantic pack.",
        "Connect a data source, or explore the bundled demo warehouse.",
        "Run discovery to generate a glossary across every table and column.",
        "Promote the pack and discovered terms you care about to Active.",
        "Ask a question in chat and save it as a report or watch it as an alert.",
      ]),
      b.h("Core concepts"),
      b.ul([
        "Glossary \u2014 an editable dictionary of what each table, column and business term means.",
        "Semantics \u2014 verified join relationships and governed metrics that ground every answer.",
        "Signals \u2014 reviewed, audit-grade records of metric breaches with frozen evidence.",
      ]),
      b.note("Press \u2318K (or Ctrl-K) anywhere in the app to jump between Schema, Glossary, Chat, Reports, Alerts, Signals, Health and Billing."),
    ],
  },
  sectorQuickstart("saas"),
  sectorQuickstart("healthcare"),
  sectorQuickstart("finserv"),
  sectorQuickstart("retail"),
  sectorQuickstart("logistics"),
  sectorQuickstart("manufacturing"),
  {
    slug: "connectors",
    title: "Connectors",
    group: "Connect your data",
    blocks: [
      b.p("Sema reads metadata and a small sample of values from your warehouse to build the semantic layer. It never copies your full dataset."),
      b.h("Supported sources"),
      b.ul([
        "PostgreSQL \u2014 live introspection of information_schema plus three sample values per column.",
        "Demo warehouse \u2014 a bundled senior-living dataset (DuckDB) for evaluating Sema with zero setup.",
        "Redshift / Snowflake / BigQuery / MySQL / REST \u2014 on the roadmap; the connector interface is source-agnostic.",
      ]),
      b.h("Connecting Postgres"),
      b.ol([
        "Open Schema \u2192 Connect Postgres.",
        "Provide host, port, database, user and password (read-only credentials recommended).",
        "Sema introspects the schema and kicks off glossary generation automatically.",
      ]),
      b.note("On the Sandbox (free) plan a workspace can connect one source. Upgrade to Pro for unlimited connectors."),
    ],
  },
  {
    slug: "data-health",
    title: "Data health",
    group: "Core features",
    blocks: [
      b.p("The System Health page reports honest, live status for every moving part of Sema."),
      b.ul([
        "Mongo \u2014 datastore connectivity.",
        "FAISS \u2014 the in-process semantic index and how many vectors it holds.",
        "Workers \u2014 the schedule and alert runners self-report heartbeats; a heartbeat older than two minutes shows as stale.",
        "Razorpay webhook \u2014 when the last billing event was received.",
        "Connectors \u2014 every connected source.",
      ]),
      b.p("Open it from the \u2318K palette \u2192 \u201cSystem health\u201d, or visit /health."),
    ],
  },
  {
    slug: "glossary",
    title: "Glossary",
    group: "Core features",
    blocks: [
      b.p("The glossary is a living dictionary. Every table and column gets an audit-grade one-sentence definition; semantic packs add business terms like MRR or Occupancy Rate."),
      b.h("Lifecycle"),
      b.ul(["Draft \u2014 newly generated or pack-installed, not yet endorsed.", "Active \u2014 reviewed and trusted.", "Deprecated \u2014 kept for history but discouraged."]),
      b.h("Sensitivity"),
      b.p("Columns are auto-classified Public / Internal / Confidential / Restricted. Restricted columns (SSN, email, DOB, \u2026) are governed by RBAC in chat."),
      b.note("Use semantic search (e.g. \u201cmonthly revenue\u201d) to find terms by meaning, not just by name."),
    ],
  },
  {
    slug: "conversational-query",
    title: "Conversational query",
    group: "Core features",
    blocks: [
      b.p("Ask a question in plain language. Sema classifies intent, plans SQL grounded in your glossary and verified joins, runs it read-only against your warehouse, and auto-charts the result."),
      b.h("What you see"),
      b.ul([
        "A streamed answer with an auto-selected chart (bar / line / stat / map).",
        "A plan inspector \u2014 the SQL, tables, joins, assumptions and a confidence score.",
        "Follow-up actions \u2014 Save as report, Watch as alert, Promote to signal.",
      ]),
      b.note("Verified joins and active metrics raise plan confidence and keep answers consistent across the team."),
    ],
  },
  {
    slug: "reports",
    title: "Reports",
    group: "Core features",
    blocks: [
      b.p("Freeze any answer as a saved report. Reports re-run the frozen SQL on fresh data, recompute deltas, re-chart and can be delivered on a schedule."),
      b.ul([
        "Delivery channels \u2014 email (live), Slack incoming-webhook, generic webhook.",
        "Schedules \u2014 cron-based with an IANA timezone and human-readable preview.",
        "Run history \u2014 every run with status, row count and a chart sparkline.",
      ]),
    ],
  },
  {
    slug: "alerts",
    title: "Alerts",
    group: "Core features",
    blocks: [
      b.p("Watch a metric and get notified when it moves. Alerts evaluate on a schedule and deliver on breach (cooldown-suppressed unless severity escalates)."),
      b.h("Methods"),
      b.ul([
        "Threshold \u2014 compare against a fixed value.",
        "Percent change \u2014 versus a point N days prior (e.g. week-over-week).",
        "Z-score \u2014 rolling-window outlier detection.",
        "Seasonal \u2014 residual versus a day-of-week / week-of-year baseline.",
      ]),
    ],
  },
  {
    slug: "signal-registry",
    title: "Signal registry",
    group: "Core features",
    blocks: [
      b.p("When a breach matters, promote it to a Signal. A reviewer approves it into the append-only registry \u2014 a compliance-grade record of \u201cdid we know, when, and who signed off.\u201d"),
      b.ul([
        "Evidence is frozen at detection time, so the record reflects what was true then.",
        "The registry is append-only \u2014 no edits or deletes (returns 405).",
        "Export to CSV, or download a per-signal PDF compliance certificate.",
      ]),
    ],
  },
  {
    slug: "rbac",
    title: "Roles & permissions",
    group: "Administration",
    blocks: [
      b.p("Every workspace has four roles: owner > admin > analyst > viewer."),
      b.ul([
        "Owner \u2014 full control including workspace deletion.",
        "Admin \u2014 manage members, connectors, LLM settings and billing.",
        "Analyst \u2014 create/edit metrics, reports, alerts and approve signals.",
        "Viewer \u2014 read-only access.",
      ]),
      b.h("Restricted columns"),
      b.p("Queries touching a Restricted column are refused for viewers and analysts (audited), and executed with masked values for admins and owners (also audited). Every governance action lands in the append-only audit log."),
    ],
  },
  {
    slug: "billing",
    title: "Billing & plans",
    group: "Administration",
    blocks: [
      b.p("Sema offers Sandbox (free), Pro, Business and Enterprise tiers. Start a 14-day Pro trial with no card required."),
      b.ul([
        "Sandbox \u2014 1 connector, 5 saved reports, 100 chat questions/month.",
        "Pro / Business \u2014 per-seat pricing (monthly or annual) with unlimited usage.",
        "Amounts are always computed server-side from seats \u00d7 plan, never trusted from the client.",
      ]),
      b.p("Checkout uses the Razorpay secure checkout modal (UPI, cards, netbanking & wallets) with auto-renewing subscriptions. When you hit a Sandbox limit, Sema surfaces an upgrade prompt instead of an error."),
      b.note("Manage your plan under Settings \u2192 Billing, or via the \u2318K palette \u2192 \u201cBilling & plan\u201d."),
    ],
  },
  {
    slug: "security",
    title: "Security overview",
    group: "Administration",
    blocks: [
      b.p("Sema is designed to read metadata, not exfiltrate data."),
      b.ul([
        "Read-only SQL firewall \u2014 generated SQL is validated and executed read-only.",
        "Encryption at rest \u2014 per-workspace LLM keys are Fernet-encrypted.",
        "Bearer-token auth with brute-force lockout and append-only audit logging.",
        "Sensitivity-aware RBAC over restricted columns.",
      ]),
    ],
  },
  {
    slug: "troubleshooting",
    title: "Troubleshooting",
    group: "Administration",
    blocks: [
      b.h("My glossary is empty"),
      b.p("A new workspace only sees terms tied to its sources or installed packs. Connect a source and run discovery, or install a semantic pack from Semantics \u2192 Packs."),
      b.h("A chat answer was refused"),
      b.p("The query likely touched a Restricted column and your role is viewer or analyst. Ask an admin, or refine the question to exclude sensitive fields."),
      b.h("Email didn't arrive"),
      b.p("In test mode email delivery is limited to the verified address; other recipients surface as \u201cfailed\u201d in run history. This is expected."),
      b.h("I hit a quota wall"),
      b.p("Sandbox limits are per-workspace. Upgrade to Pro from Settings \u2192 Billing to lift them."),
    ],
  },
];

export const DOC_GROUPS = DOCS.reduce((acc, d) => {
  (acc[d.group] = acc[d.group] || []).push(d);
  return acc;
}, {});

export function findDoc(slug) {
  return DOCS.find((d) => d.slug === slug) || DOCS[0];
}
